import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Mail, ExternalLink, X, Calendar, Code, Users, Loader2, Moon, Sun, Shield, Zap, Database, Cpu, FileText, BookOpen, Globe, Linkedin, MapPin } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ResearchPage from './ResearchPage';

const HomePage = ({ darkMode, toggleDarkMode }) => {
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [readme, setReadme] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showReadme, setShowReadme] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);

  // Personal details
  const personalInfo = {
    name: 'Utsav Mistry',
    title: 'Cyber & Systems Engineer',
    tagline: 'Building secure, modern, and efficient systems.',
    email: 'utsavamistry30@gmail.com',
    github: 'utsav-mistry',
    linkedin: 'utsav-mistry-3056772b6',
    location: 'India',
    about: 'Innovative full-stack developer focused on building secure, scalable, and AI-driven solutions. My areas of expertise include FinTech, Cybersecurity, and Applied AI. I specialize in developing tools and platforms that prioritize security, performance, and real-world impact.',
    skills: ['React', 'Node.js', 'Python', 'Flask', 'Django', 'PostgreSQL', 'MongoDB', 'Docker', 'AWS', 'Cybersecurity', 'Machine Learning', 'AI/ML']
  };

  useEffect(() => {
    fetchRepos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight;
      const scrollThreshold = heroHeight * 0.2; // 20% of hero height

      setShowNavbar(scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchRepos = async () => {
    try {
      setLoading(true);
      setError('');

      // Read from details.txt file in public folder
      const response = await fetch('/details.txt');
      if (!response.ok) {
        throw new Error('Failed to load repository details');
      }

      const reposData = await response.json();

      // Filter to show only public repos
      const publicRepos = reposData.filter(repo => !repo.private);

      setRepos(publicRepos);
      console.log(`Loaded ${publicRepos.length} public repositories from details.txt`);
    } catch (err) {
      setError('Failed to load repository details. Please run the extractor first.');
      console.error('Error loading repos:', err);
      // Set some sample repos for demo purposes
      setRepos([
        {
          name: 'portfolio',
          description: 'My personal portfolio website built with React and Tailwind CSS',
          html_url: 'https://github.com/utsav-mistry/portfolio',
          updated_at: new Date().toISOString(),
          language: 'JavaScript',
          stargazers_count: 0,
          readme_content: '# Portfolio\n\nMy personal portfolio website built with React and Tailwind CSS.'
        },
        {
          name: 'sample-project',
          description: 'A sample project showcasing my development skills',
          html_url: 'https://github.com/utsav-mistry/sample-project',
          updated_at: new Date().toISOString(),
          language: 'Python',
          stargazers_count: 0,
          readme_content: '# Sample Project\n\nA sample project showcasing my development skills.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReadme = async (repoFullName) => {
    try {
      setLoading(true);
      setError('');

      // Find the repo in our loaded data
      const repo = repos.find(r => r.full_name === repoFullName);

      if (repo && repo.readme_content) {
        setReadme(repo.readme_content);
      } else {
        setReadme('');
        setError('README not available');
      }
    } catch (err) {
      setReadme('');
      setError('README not available');
      console.error('Error loading README:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRepoClick = async (repo) => {
    setSelectedRepo(repo);
    setShowReadme(true);
    await fetchReadme(repo.full_name);
  };

  const closeReadme = () => {
    setShowReadme(false);
    setSelectedRepo(null);
    setReadme('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-200">
      {/* Header */}
      <AnimatePresence>
        {showNavbar && (
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-0 left-0 right-0 bg-white/95 dark:bg-black/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-900 z-50"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center space-x-8">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    {personalInfo.name}
                  </h1>
                  <nav className="hidden md:flex space-x-6">
                    <a href="#about" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">About</a>
                    <a href="#projects" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Projects</a>
                    <a href="#research" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Research</a>
                    <a href="#contact" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Contact</a>
                  </nav>
                </div>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>

      {/* Dark Mode Toggle - Fixed Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleDarkMode}
          className="p-3 rounded-full bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
        >
          {darkMode ? <Sun size={24} /> : <Moon size={24} />}
        </button>
      </div>

      {/* Hero Section - Split Layout */}
      <section className="min-h-screen flex items-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-black dark:via-gray-900 dark:to-black relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-5">
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  {personalInfo.name}
                </h1>
                <h2 className="text-2xl md:text-3xl lg:text-4xl text-gray-700 dark:text-gray-300 mb-6">
                  {personalInfo.title}
                </h2>
                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  {personalInfo.tagline}
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                {personalInfo.skills.slice(0, 6).map((skill, index) => (
                  <motion.span
                    key={skill}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="px-4 py-2 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-800 shadow-sm"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 relative z-10">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Opening contact modal');
                    setShowContactModal(true);
                  }}
                  className="btn btn-primary flex items-center justify-center space-x-2 text-lg px-8 py-4 cursor-pointer select-none"
                >
                  <Mail size={20} />
                  <span>Get in Touch</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    console.log('Opening GitHub profile');
                    const url = `https://github.com/${personalInfo.github}`;
                    console.log('GitHub URL:', url);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  className="btn btn-secondary flex items-center justify-center space-x-2 text-lg px-8 py-4 cursor-pointer select-none"
                >
                  <Github size={20} />
                  <span>GitHub</span>
                </button>
              </div>
            </motion.div>

            {/* Right Side - Graphics */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                    <Shield className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Security</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Cybersecurity & Encryption</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                    <Cpu className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">AI/ML</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Machine Learning</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                    <Database className="h-12 w-12 text-purple-600 dark:text-purple-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Full-Stack</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Web Development</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="text-center"
                >
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                    <Globe className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Cloud</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">AWS & Deployment</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-gray-400 dark:bg-gray-600 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">About Me</h2>
            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
              {personalInfo.about}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
            >
              <Shield className="h-16 w-16 text-blue-600 dark:text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Cybersecurity</h3>
              <p className="text-gray-700 dark:text-gray-300">Expert in building secure systems and implementing best practices for data protection.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
            >
              <Zap className="h-16 w-16 text-green-600 dark:text-green-400 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Full-Stack Development</h3>
              <p className="text-gray-700 dark:text-gray-300">Building modern web applications with React, Node.js, and Python frameworks.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
            >
              <Code className="h-16 w-16 text-purple-600 dark:text-purple-400 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">AI & ML</h3>
              <p className="text-gray-700 dark:text-gray-300">Integrating machine learning and AI solutions for real-world applications.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">My Projects</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Explore my open-source projects with documentation
            </p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
            </div>
          ) : repos.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 dark:text-gray-400">
                No projects with README files found.
              </p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
              {repos.map((repo, index) => (
                <motion.div
                  key={repo.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-white dark:bg-gray-900 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer border border-gray-200 dark:border-gray-800 group break-inside-avoid mb-8 ${repo.is_contribution ? 'border-l-4 border-l-green-500' : ''}`}
                  onClick={() => handleRepoClick(repo)}
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {repo.name}
                        </h3>
                        {repo.is_contribution && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Contribution to @{repo.owner}
                          </p>
                        )}
                      </div>
                      <Github size={24} className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors ml-4" />
                    </div>

                    {repo.description && (
                      <p className="text-gray-700 dark:text-gray-300 text-sm mb-6 line-clamp-3">
                        {repo.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Calendar size={16} className="mr-2" />
                        {formatDate(repo.updated_at)}
                      </span>
                      <span className="flex items-center">
                        <Code size={16} className="mr-2" />
                        {repo.language || 'N/A'}
                      </span>
                    </div>

                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Users size={16} className="mr-2" />
                        {repo.stargazers_count} stars
                      </span>
                      {repo.stargazers_count > 0 && (
                        <span className="flex items-center text-yellow-500">
                          ⭐
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Research Section */}
      <section id="research" className="py-20 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Research & Innovation</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Exploring cutting-edge AI technologies and their applications in cybersecurity
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-xl"
            >
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 dark:bg-blue-500 p-3 rounded-xl mr-4">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Project AIGES</h3>
                  <p className="text-gray-600 dark:text-gray-400">Context-Aware AI Coding Systems</p>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                Foundational research toward context-aware AI coding systems that eliminate hallucination through complete
                repository knowledge. This comprehensive study analyzes existing AI coding tools and demonstrates a
                multi-layered architecture to transform AI assistants into grounded co-developers, achieving meaningful
                reductions in coding errors and review overhead in real-world scenarios.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">847</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Code Requests</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">3</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">AI Tools Evaluated</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">76%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Fewer Critical Errors</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Weeks of Experimentation</div>
                </div>
              </div>

              <div className="flex justify-center">
                <Link
                  to="/research"
                  className="btn btn-primary flex items-center justify-center space-x-2 text-lg px-8 py-4"
                >
                  <BookOpen size={20} />
                  <span>Read Full Paper</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */
        <section id="contact" className="py-20 bg-white dark:bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Get In Touch</h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                I'm always open to discussing new opportunities, interesting projects, or just having a chat about technology.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
              >
                <Mail className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Email</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">{personalInfo.email}</p>
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="btn btn-primary"
                >
                  Send Email
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
              >
                <Github className="h-12 w-12 text-gray-600 dark:text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">GitHub</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Check out my projects and contributions</p>
                <a
                  href={`https://github.com/${personalInfo.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  View Profile
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
              >
                <Linkedin className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">LinkedIn</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">Connect with me professionally</p>
                <a
                  href="https://www.linkedin.com/in/utsav-mistry-3056772b6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  Connect
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center mt-12 p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
            >
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Let's Work Together</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
                Whether you have a project in mind, want to collaborate, or just want to say hello, I'd love to hear from you.
                I'm particularly interested in cybersecurity, AI/ML, and full-stack development opportunities.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="btn btn-primary flex items-center justify-center space-x-2"
                >
                  <Mail size={20} />
                  <span>Start a Conversation</span>
                </a>
                <a
                  href={`https://github.com/${personalInfo.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary flex items-center justify-center space-x-2"
                >
                  <Github size={20} />
                  <span>View My Work</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      }
      {/* README Modal */}
      <AnimatePresence>
        {showReadme && selectedRepo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={closeReadme}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl shadow-2xl max-w-[95vw] sm:max-w-6xl w-full max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <Github size={28} className="text-gray-600 dark:text-gray-400 sm:w-8 sm:h-8" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                        {selectedRepo.name}
                      </h2>
                      {selectedRepo.description && (
                        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base leading-relaxed break-words">
                          {selectedRepo.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-2 sm:space-x-3 flex-shrink-0">
                  <a
                    href={selectedRepo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary flex items-center space-x-2 text-sm sm:text-base px-3 sm:px-4 py-2"
                  >
                    <Github size={14} className="sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">View on GitHub</span>
                    <span className="sm:hidden">GitHub</span>
                    <ExternalLink size={14} className="sm:w-4 sm:h-4" />
                  </a>
                  <button
                    onClick={closeReadme}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X size={18} className="sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(95vh-120px)]">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">{error}</p>
                  </div>
                ) : readme ? (
                  <div className="markdown-content max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full" {...props} />
                          </div>
                        )
                      }}
                    >
                      {readme}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">Loading README...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl shadow-2xl max-w-[95vw] sm:max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Get In Touch</h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm sm:text-base leading-relaxed">
                    I'm always open to discussing new opportunities, interesting projects, or just having a chat about technology.
                  </p>
                </div>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="self-end sm:self-start p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                >
                  <X size={18} className="sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(90vh-140px)] sm:max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
                  <div className="text-center p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-800">
                    <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 dark:text-blue-400 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">Email</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base break-all">{personalInfo.email}</p>
                    <a
                      href={`mailto:${personalInfo.email}`}
                      className="btn btn-primary text-sm sm:text-base px-3 sm:px-4 py-2"
                    >
                      Send Email
                    </a>
                  </div>

                  <div className="text-center p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-800">
                    <Github className="h-10 w-10 sm:h-12 sm:w-12 text-gray-600 dark:text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">GitHub</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base">Check out my projects and contributions</p>
                    <a
                      href={`https://github.com/${personalInfo.github}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary text-sm sm:text-base px-3 sm:px-4 py-2"
                    >
                      View Profile
                    </a>
                  </div>

                  <div className="text-center p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-800 sm:col-span-2 lg:col-span-1">
                    <Linkedin className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 dark:text-blue-400 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">LinkedIn</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 text-sm sm:text-base">Connect with me professionally</p>
                    <a
                      href="https://www.linkedin.com/in/utsav-mistry-3056772b6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary text-sm sm:text-base px-3 sm:px-4 py-2"
                    >
                      Connect
                    </a>
                  </div>
                </div>

                <div className="text-center p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-800">
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Let's Work Together</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
                    Whether you have a project in mind, want to collaborate, or just want to say hello, I'd love to hear from you.
                    I'm particularly interested in cybersecurity, AI/ML, and full-stack development opportunities.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                    <a
                      href={`mailto:${personalInfo.email}`}
                      className="btn btn-primary flex items-center justify-center space-x-2 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                    >
                      <Mail size={16} className="sm:w-5 sm:h-5" />
                      <span>Start a Conversation</span>
                    </a>
                    <a
                      href={`https://github.com/${personalInfo.github}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary flex items-center justify-center space-x-2 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                    >
                      <Github size={16} className="sm:w-5 sm:h-5" />
                      <span>View My Work</span>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer - Redesigned */}
      <footer className="bg-gray-900 dark:bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold mb-4">{personalInfo.name}</h3>
            <p className="text-xl text-gray-400 mb-8">{personalInfo.tagline}</p>

            <div className="flex justify-center space-x-6 mb-8">
              <a
                href={`https://github.com/${personalInfo.github}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors p-3 rounded-full hover:bg-gray-800"
              >
                <Github size={28} />
              </a>
              <a
                href="https://www.linkedin.com/in/utsav-mistry-3056772b6"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors p-3 rounded-full hover:bg-gray-800"
              >
                <Linkedin size={28} />
              </a>
              <a
                href={`mailto:${personalInfo.email}`}
                className="text-gray-400 hover:text-white transition-colors p-3 rounded-full hover:bg-gray-800"
              >
                <Mail size={28} />
              </a>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 {personalInfo.name}. All rights reserved.
              </div>
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <span className="flex items-center">
                  <MapPin size={16} className="mr-2" />
                  {personalInfo.location}
                </span>
                <span className="flex items-center">
                  <Mail size={16} className="mr-2" />
                  {personalInfo.email}
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode);
    document.documentElement.classList.toggle('dark', newDarkMode);
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={<HomePage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />}
        />
        <Route
          path="/research"
          element={<ResearchPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />}
        />
      </Routes>
    </Router>
  );
};

export default App;
